/**
 * AdminSuiviPrelevementsSEPA — Tableau de bord admin des prélèvements SEPA.
 * Liste les échéances à venir / en cours / en échec (rail Stripe).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import BackButton from '../components/BackButton';
import { CalendarClock, AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  scheduled: { label: 'À venir', cls: 'bg-amber-100 text-amber-700' },
  processing: { label: 'En cours', cls: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Payé', cls: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Échec', cls: 'bg-rose-100 text-rose-700' },
};

const FILTERS = [
  { key: 'upcoming', label: 'À venir' },
  { key: 'processing', label: 'En cours' },
  { key: 'failed', label: 'En échec' },
  { key: 'all', label: 'Toutes' },
];

const euros = (c) => `${((c || 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso || '—'; } };

export default function AdminSuiviPrelevementsSEPA() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('upcoming');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sepa-stripe/admin/echeances?filter=${filter}`, { headers: getAuthHeader() });
      setRows(res.data?.echeances || []);
      setCounts(res.data?.counts || {});
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="admin-suivi-sepa-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <BackButton />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarClock className="h-5 w-5 text-[#DEA900]" /> Suivi des prélèvements SEPA
            </CardTitle>
            <Button variant="outline" size="sm" onClick={load} data-testid="sepa-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700"><Clock className="h-4 w-4" /> À venir : <b>{counts.upcoming ?? 0}</b></div>
              <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700"><Clock className="h-4 w-4" /> En cours : <b>{counts.processing ?? 0}</b></div>
              <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700"><AlertTriangle className="h-4 w-4" /> En échec : <b>{counts.failed ?? 0}</b></div>
            </div>
            <div className="flex gap-2">
              {FILTERS.map((f) => (
                <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'outline'}
                  onClick={() => setFilter(f.key)} data-testid={`sepa-filter-${f.key}`}>{f.label}</Button>
              ))}
            </div>
            {loading ? (
              <p className="text-sm text-slate-400 py-8 text-center">Chargement…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center" data-testid="sepa-empty">Aucune échéance.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sepa-echeances-table">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 border-b">
                      <th className="py-2 pr-3">Souscription</th>
                      <th className="py-2 pr-3">Membre</th>
                      <th className="py-2 pr-3">Éch.</th>
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3 text-right">Montant</th>
                      <th className="py-2 pr-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const st = STATUS[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-600' };
                      return (
                        <tr key={i} className="border-b border-slate-100" data-testid={`sepa-row-${r.souscription_id}-${r.index}`}>
                          <td className="py-2 pr-3 font-mono text-xs">{r.reference}</td>
                          <td className="py-2 pr-3">{r.customer_name || r.customer_email}</td>
                          <td className="py-2 pr-3">#{r.index}{r.retry_count ? ` (tentative ${r.retry_count})` : ''}</td>
                          <td className="py-2 pr-3">{fmtDate(r.charge_date)}</td>
                          <td className="py-2 pr-3 text-right font-medium">{euros(r.amount_cents)}</td>
                          <td className="py-2 pr-3"><Badge className={st.cls}>{st.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
